// Centralized gallery of every image uploaded from the GM console.
// Reads /admin/gm/images/list, uploads via /admin/gm/images/upload,
// and can delete unused files. Files live under backend/assets/gm_images
// so they ship with the source tree and survive redeploys.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Search, Copy, Image as ImageIcon, RefreshCw } from "lucide-react";
import { api, API } from "@/lib/api";
import { gmStore } from "@/gm/gmStore";

const RESOLVE = (url) => (url?.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`);

export default function GraphicsPanel() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [refs, setRefs] = useState({});   // url → [{kind,name}]

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.gmListImages();
      setImages(r.images || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile caricare le grafiche");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Build a reverse index: which enemy/item/class/world_boss references
  // each URL. This is client-side only (fast) so we don't need a new API.
  useEffect(() => {
    const map = {};
    const add = (url, kind, name) => {
      if (!url) return;
      (map[url] ||= []).push({ kind, name });
    };
    for (const e of gmStore.enemies.list())      add(e.image, "Nemico", e.name);
    for (const i of gmStore.items.list())        add(i.image, "Oggetto", i.name);
    for (const c of gmStore.classes.list())      add(c.image, "Classe", c.name);
    for (const b of gmStore.world_bosses.list()) add(b.image, "World Boss", b.name);
    setRefs(map);
  }, [images]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return images;
    return images.filter(i =>
      i.name.toLowerCase().includes(t) ||
      (refs[i.url] || []).some(r => (r.name || "").toLowerCase().includes(t))
    );
  }, [images, q, refs]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Max 4MB");
    try {
      const r = await api.gmUploadImage(file);
      toast.success(`Caricata: ${r.name}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload fallito");
    }
  };

  const onDelete = async (img) => {
    const uses = refs[img.url] || [];
    if (uses.length > 0) {
      if (!window.confirm(`"${img.name}" è usata da ${uses.length} entità. Eliminare comunque? Le entità mostreranno il placeholder.`)) return;
    } else if (!window.confirm(`Eliminare "${img.name}"?`)) return;
    try {
      await api.gmDeleteImage(img.name);
      toast.success("Eliminata");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile eliminare");
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url).then(
      () => toast.success("URL copiato"),
      () => toast.error("Copia non riuscita"),
    );
  };

  const totalBytes = images.reduce((n, i) => n + (i.size || 0), 0);
  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`;

  return (
    <div className="space-y-3" data-testid="gm-graphics-section">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
        <div>
          <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">Grafiche</div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
            {images.length} file · {fmtSize(totalBytes)} · backend/assets/gm_images
          </div>
        </div>
        <div className="sm:ml-auto flex flex-wrap gap-2">
          <button data-testid="gm-graphics-refresh" onClick={load}
            className="px-3 py-2 border border-[#262626] text-[#B0ADA5] hover:text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" /> Ricarica
          </button>
          <label className="px-3 py-2 border border-[#B89C3A] text-[#E5C34A] hover:bg-[#B89C3A]/10 font-heading text-[10px] tracking-widest uppercase flex items-center gap-1.5 cursor-pointer">
            <Upload className="w-3 h-3" /> Carica immagine
            <input data-testid="gm-graphics-upload" type="file" accept="image/*" onChange={onUpload} className="hidden" />
          </label>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#666]" strokeWidth={1.5} />
        <input data-testid="gm-graphics-search" value={q} onChange={e => setQ(e.target.value)}
          placeholder="Cerca per nome file o entità collegata..."
          className="w-full bg-[#0a0a0a] border border-[#333] pl-8 pr-3 py-2 text-xs text-[#E2DFD8] outline-none focus:border-[#B89C3A]" />
      </div>

      {loading ? (
        <div className="text-center text-xs italic text-[#666] py-12">Caricamento grafiche...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs italic text-[#666] py-12" data-testid="gm-graphics-empty">
          {images.length === 0
            ? "Nessuna immagine ancora caricata. Usa Carica immagine per iniziare."
            : "Nessuna immagine corrisponde alla ricerca."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map(img => {
            const uses = refs[img.url] || [];
            const orphan = uses.length === 0;
            return (
              <div key={img.name} data-testid={`gm-graphics-card-${img.name}`}
                className={`panel p-2 space-y-2 border ${orphan ? "border-[#333]" : "border-[#B89C3A]/40"}`}>
                <div className="aspect-square bg-[#0a0a0a] border border-[#262626] flex items-center justify-center overflow-hidden">
                  <img src={RESOLVE(img.url)} alt={img.name} className="max-w-full max-h-full object-contain"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </div>
                <div className="font-mono text-[9px] text-[#B0ADA5] truncate" title={img.name}>{img.name}</div>
                <div className="font-mono text-[8px] uppercase tracking-widest text-[#666]">{fmtSize(img.size)}</div>
                {uses.length > 0 ? (
                  <div className="font-mono text-[8px] text-[#E5C34A]">
                    {uses.slice(0, 3).map((u, i) => <div key={i}>{u.kind}: {u.name || "?"}</div>)}
                    {uses.length > 3 && <div className="text-[#666]">+ altre {uses.length - 3}</div>}
                  </div>
                ) : (
                  <div className="font-mono text-[8px] uppercase tracking-widest text-[#666]">orfana</div>
                )}
                <div className="flex gap-1 pt-1 border-t border-[#262626]">
                  <button onClick={() => copyUrl(img.url)} data-testid={`gm-graphics-copy-${img.name}`}
                    className="flex-1 font-mono text-[9px] uppercase tracking-widest text-[#B89C3A] hover:text-[#E5C34A] flex items-center justify-center gap-1">
                    <Copy className="w-3 h-3" /> URL
                  </button>
                  <button onClick={() => onDelete(img)} data-testid={`gm-graphics-del-${img.name}`}
                    className="flex-1 font-mono text-[9px] uppercase tracking-widest text-[#E33333] hover:text-[#8A0303] flex items-center justify-center gap-1">
                    <Trash2 className="w-3 h-3" /> Elimina
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="panel p-3 text-[10px] font-mono text-[#666] flex items-start gap-2">
        <ImageIcon className="w-3 h-3 shrink-0 mt-0.5" />
        <div>
          Ogni immagine caricata viene salvata su file system in <span className="text-[#E5C34A]">backend/assets/gm_images/</span> e resta anche dopo un redeploy.
          Le immagini vecchie in base64 vengono convertite automaticamente al primo salvataggio.
        </div>
      </div>
    </div>
  );
}
